// Read-only task categories, not a financial record count. Overlapping alert/source domains share one category.
type SourceStatus = 'success' | 'pending' | 'error';
type Source = { status: SourceStatus; hasItems: boolean };
export type TaskCategory = 'receipts' | 'schedule' | 'savings' | 'reminders';
interface TaskSources { receipts: Source; schedule: Source; savings: Source; alerts: { status: SourceStatus; types: string[] } }
const categoryOrder: TaskCategory[] = ['receipts', 'schedule', 'savings', 'reminders'];
function alertCategory(type: string): TaskCategory {
  if (['duplicate_receipt', 'big_ticket'].includes(type)) return 'receipts';
  if (['pending_overdue', 'income_missed', 'fixed_due_today'].includes(type)) return 'schedule';
  if (['savings_due_weekly', 'pay_yourself_paycheck'].includes(type)) return 'savings';
  return 'reminders';
}
export function getTaskOverview(sources: TaskSources) {
  const categories = new Set<TaskCategory>();
  for (const category of ['receipts', 'schedule', 'savings'] as const) if (sources[category].status === 'success' && sources[category].hasItems) categories.add(category);
  if (sources.alerts.status === 'success') for (const type of sources.alerts.types) categories.add(alertCategory(type));
  const complete = Object.values(sources).every(source => source.status === 'success');
  return {
    categories: categoryOrder.filter(category => categories.has(category)),
    categoryCount: complete ? categories.size : null,
    complete,
    allHandled: complete && categories.size === 0,
    hasError: Object.values(sources).some(source => source.status === 'error'),
    isChecking: Object.values(sources).some(source => source.status === 'pending'),
  };
}
interface SavingsTaskPlan {
  plan: { bank_sync_required: number };
  totals: { automatic_unverified_minor: number };
  occurrences: Array<{ status: string; due_date: string; amount_minor: number; deposited_minor: number }>;
}
export function getSavingsTasks<T extends SavingsTaskPlan>(data: { today: string; plans: T[] } | undefined): Array<T & { dueCount: number }> {
  return data?.plans.map(item => ({ ...item, dueCount: item.occurrences.filter(occurrence => occurrence.status === 'pending' && occurrence.due_date <= data.today && occurrence.deposited_minor < occurrence.amount_minor).length }))
    .filter(item => item.dueCount > 0 || item.plan.bank_sync_required > 0 || item.totals.automatic_unverified_minor > 0) ?? [];
}
